import axios from 'axios';
import properties from '../properties';

/**
 * Get JWT token from axios defaults (same token used by other API calls)
 */
const getAuthToken = (): string | null => {
  try {
    // Get token from axios defaults - this is set globally in AppWithRouteAccess.tsx
    const token = axios.defaults.headers.common['Authorization'] as string;
    if (token) {
      return token;
    }

    // Fallback to localStorage if axios default is not set
    const storageToken = JSON.parse(
      localStorage.getItem('okta-token-storage') as string,
    )?.accessToken?.accessToken;
    return storageToken || null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

export interface ChatMessage {
  _id?: string;
  file_metadata_id?: string;
  file_url: string;
  file_id?: string;
  message: string;
  response: string;
  message_type?: 'user' | 'system';
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ChatHistoryResponse {
  data: ChatMessage[];
  count: number;
  limit: number;
  skip: number;
  status: string;
}

export interface CreateChatResponse {
  data: ChatMessage;
  status: string;
  message: string;
}

export interface ChatStatsResponse {
  data: {
    total_chats: number;
    file_metadata: any;
    has_metadata: boolean;
    pinecone_namespace_id: string | null;
  };
  status: string;
}

/**
 * Create a new chat message
 * @param fileUrl - URL of the document
 * @param message - User's message
 * @param fileId - Optional file ID
 * @returns Promise with the created chat message
 */
export const createChatMessage = async (
  fileUrl: string,
  message: string,
  fileId?: string,
): Promise<CreateChatResponse> => {
  try {
    // Axios already has Authorization header set globally, no need to manually add it
    const response = await axios.post(`${properties.aiqBaseUrl}/api/v1/chat`, {
      file_url: fileUrl,
      file_id: fileId,
      message: message,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error creating chat message:', error);
    throw new Error(
      error?.response?.data?.error || 'Failed to create chat message',
    );
  }
};

/**
 * Get chat history for a document
 * @param fileUrl - URL of the document
 * @param fileId - Optional file ID
 * @param limit - Maximum number of chats to retrieve
 * @param skip - Number of chats to skip
 * @returns Promise with chat history
 */
export const getChatHistory = async (
  fileUrl: string,
  fileId?: string,
  limit: number = 100,
  skip: number = 0,
): Promise<ChatHistoryResponse> => {
  try {
    const params: any = {
      file_url: fileUrl,
      limit,
      skip,
    };

    if (fileId) {
      params.file_id = fileId;
    }

    // Axios already has Authorization header set globally
    const response = await axios.get(`${properties.aiqBaseUrl}/api/v1/chat`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error getting chat history:', error);
    throw new Error(
      error?.response?.data?.error || 'Failed to get chat history',
    );
  }
};

/**
 * Get chat statistics for a document
 * @param fileUrl - URL of the document
 * @param fileId - Optional file ID
 * @returns Promise with chat statistics
 */
export const getChatStats = async (
  fileUrl: string,
  fileId?: string,
): Promise<ChatStatsResponse> => {
  try {
    const params: any = {
      file_url: fileUrl,
    };

    if (fileId) {
      params.file_id = fileId;
    }

    // Axios already has Authorization header set globally
    const response = await axios.get(
      `${properties.aiqBaseUrl}/api/v1/chat/stats`,
      { params },
    );
    return response.data;
  } catch (error: any) {
    console.error('Error getting chat stats:', error);
    throw new Error(
      error?.response?.data?.error || 'Failed to get chat statistics',
    );
  }
};

/**
 * Delete all chats for a document
 * @param fileUrl - URL of the document
 * @param fileId - Optional file ID
 * @returns Promise with deletion result
 */
export const deleteChats = async (
  fileUrl: string,
  fileId?: string,
): Promise<any> => {
  try {
    // Axios already has Authorization header set globally
    const response = await axios.delete(
      `${properties.aiqBaseUrl}/api/v1/chat`,
      {
        data: {
          file_url: fileUrl,
          file_id: fileId,
        },
      },
    );
    return response.data;
  } catch (error: any) {
    console.error('Error deleting chats:', error);
    throw new Error(error?.response?.data?.error || 'Failed to delete chats');
  }
};

/**
 * Stream a chat message using LangGraph API with Server-Sent Events
 * @param threadId - Unique thread ID for the conversation
 * @param messages - Array of previous messages in the conversation
 * @param fileMetadata - File context metadata (file_url, file_id, document_name, etc.)
 * @param onChunk - Callback function called for each chunk of streamed data
 * @param onComplete - Callback function called when streaming completes
 * @param onError - Callback function called if an error occurs
 * @returns Function to abort the stream
 */
export const streamChatMessage = (
  threadId: string,
  messages: Array<{ id: string; type: 'human' | 'ai'; content: string }>,
  fileMetadata: {
    file_url: string;
    file_id: string;
    document_name?: string;
    contract_id?: string;
    contract_type?: string;
  },

  onChunk: (chunk: any) => void,
  onComplete: () => void,

  onError: (error: Error) => void,
): (() => void) => {
  const token = getAuthToken();

  if (!token) {
    onError(new Error('Authentication token not found'));
    return () => {};
  }

  const controller = new AbortController();
  const signal = controller.signal;

  // Build the API URL using the same base URL as other API calls
  const apiUrl = `${properties.aiqBaseUrl}/api/v1/chat-lg/threads/${threadId}/runs/stream`;

  // Start the streaming request
  fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: token,
    },
    body: JSON.stringify({
      input: {
        messages: messages,
      },
      config: {
        metadata: {
          file_url: fileMetadata.file_url,
          file_id: fileMetadata.file_id,
          document_name: fileMetadata.document_name,
          contract_id: fileMetadata.contract_id,
          contract_type: fileMetadata.contract_type,
        },
      },
      stream_mode: ['values'],
      stream_subgraphs: true,
    }),
    signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            continue;
          }

          if (line.startsWith('data:')) {
            const data = line.replace('data:', '').trim();

            if (data === 'null' || data === '') {
              continue;
            }

            try {
              const parsed = JSON.parse(data);
              onChunk(parsed);
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }

      onComplete();
    })
    .catch((error) => {
      if (error.name !== 'AbortError') {
        onError(error);
      }
    });

  // Return abort function
  return () => controller.abort();
};

/**
 * Initialize chat for a specific file
 * @param fileId - File ID to initialize chat for
 * @param fileType - Type of file ('contract' or 'plan_doc')
 * @returns Promise with initialization response
 */
export const initialiseChatWithFile = async (
  fileId: string,
  fileType?: string,
): Promise<any> => {
  try {
    const payload: any = {
      file_id: fileId,
    };

    if (fileType) {
      payload.file_type = fileType;
    }

    const response = await axios.post(
      `${properties.aiqBaseUrl}/api/v1/chat-lg/initialise-chat`,
      payload,
    );
    return response.data;
  } catch (error: any) {
    console.error('Error initialising chat:', error);
    throw new Error(
      error?.response?.data?.error || 'Failed to initialise chat',
    );
  }
};

/**
 * Send a chat message using LangGraph API wait endpoint (non-streaming)
 * @param threadId - Unique thread ID for the conversation
 * @param messages - Array of previous messages in the conversation
 * @param fileMetadata - File context metadata (file_url, file_id, document_name, etc.)
 * @returns Promise with the complete response
 */
export const sendChatMessage = async (
  threadId: string,
  messages: Array<{ id: string; type: 'human' | 'ai'; content: string }>,
  fileMetadata: {
    file_url: string;
    file_id: string;
    document_name?: string;
    contract_id?: string;
    contract_type?: string;
  },
): Promise<any> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Authentication token not found');
  }

  // Build the API URL using the wait endpoint
  const apiUrl = `${properties.aiqBaseUrl}/api/v1/chat-lg/threads/${threadId}/runs/wait`;

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({
        input: {
          messages: messages,
        },
        config: {
          metadata: {
            file_url: fileMetadata.file_url,
            file_id: fileMetadata.file_id,
            document_name: fileMetadata.document_name,
            contract_id: fileMetadata.contract_id,
            contract_type: fileMetadata.contract_type,
          },
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error sending chat message:', error);
    throw new Error(error?.message || 'Failed to send chat message');
  }
};
